/**
 * promo-progress.js — Locket Loves "3 for £50"
 *
 * Shapes already re-fetches its cart sections on every add and every quantity
 * change, and hands the fresh HTML to listeners. We ride that: no window.fetch
 * patching, no polling, no second add-to-cart path.
 *
 * Events used (all Shapes' own, see assets/theme-events.js and the Switch
 * developer docs at help.switchthemes.co/shapes/developer/theme-events):
 *
 *   window   shapes:cart:afteradditem   \
 *   window   shapes:cart:cartqtychange   >  carry detail.response.sections
 *   window   shapes:cart:update         /   including the freshly rendered
 *                                           `cart-items` section.
 *   document theme:cart:change              public aggregate event, used only
 *                                           as a safety net when no payload
 *                                           arrived.
 *
 * The module inside [data-cart-items] needs no help at all: Shapes runs
 * Alpine.morph over that subtree with the new server HTML, so it repaints
 * itself. This script exists for the copies that live outside the cart, i.e.
 * the one on the product page.
 */

(() => {
  'use strict';

  // The snippet renders once per placement, so this file can be requested more
  // than once on a page. Only ever wire up the listeners a single time.
  if (window.__promoProgressReady) return;
  window.__promoProgressReady = true;

  const ROOT_SELECTOR = 'promo-progress[data-promo-progress]';
  const CART_ROOT = '[data-cart-items]';
  const DISMISS_KEY = 'locketloves:promo-progress:dismissed';
  const DISMISS_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days

  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  /* ------------------------------------------------------------------ utils */

  const parse = (html) =>
    new DOMParser().parseFromString(html, 'text/html');

  /** Every module that Shapes will NOT repaint for us. */
  const externalModules = () =>
    Array.from(document.querySelectorAll(ROOT_SELECTOR)).filter(
      (el) => !el.closest(CART_ROOT)
    );

  const readState = (root) => {
    const tag = root.querySelector('script[type="application/json"][data-promo-state]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent);
    } catch (e) {
      return null;
    }
  };

  /* ----------------------------------------------------------------- repaint */

  /**
   * Copy the meter values out of a freshly server-rendered module and into a
   * live one. Deliberately field by field: the product page module contains
   * hydrated <data-island> quick-buy components, and replacing markup would
   * tear them down and lose the shopper's variant selection.
   */
  const repaint = (target, fresh) => {
    const state = readState(fresh);
    const previous = Number(target.dataset.promoInSet || 0);

    // Slots. These are plain images and text, safe to swap wholesale.
    const freshSlots = fresh.querySelector('[data-promo-slots]');
    const targetSlots = target.querySelector('[data-promo-slots]');
    if (freshSlots && targetSlots) {
      targetSlots.innerHTML = freshSlots.innerHTML;
      targetSlots.setAttribute('aria-label', freshSlots.getAttribute('aria-label') || '');
      targetSlots.classList.toggle(
        'promo-progress__slots--complete',
        freshSlots.classList.contains('promo-progress__slots--complete')
      );

      // Nudge only the slot that just filled, and only if motion is welcome.
      if (state && !reduceMotion && state.inSet > previous) {
        const justFilled = targetSlots.children[state.inSet - 1];
        if (justFilled) {
          justFilled.classList.add('promo-progress__slot--just-filled');
          justFilled.addEventListener(
            'animationend',
            () => justFilled.classList.remove('promo-progress__slot--just-filled'),
            { once: true }
          );
        }
      }
    }

    // Text fields.
    const swapHTML = (selector) => {
      const a = fresh.querySelector(selector);
      const b = target.querySelector(selector);
      if (a && b) b.innerHTML = a.innerHTML;
    };

    swapHTML('[data-promo-message]');
    swapHTML('[data-promo-save]');
    swapHTML('[data-promo-ship-message]');
    swapHTML('[data-promo-cta-label]');

    // Call to action visibility.
    const freshCta = fresh.querySelector('[data-promo-cta]');
    const targetCta = target.querySelector('[data-promo-cta]');
    if (freshCta && targetCta) {
      targetCta.hidden = freshCta.hasAttribute('hidden');
    }

    // Free shipping bar.
    const freshFill = fresh.querySelector('[data-promo-ship-fill]');
    const targetFill = target.querySelector('[data-promo-ship-fill]');
    if (freshFill && targetFill) {
      targetFill.style.width = freshFill.style.width;
    }

    // Root state.
    target.classList.toggle('is-complete', fresh.classList.contains('is-complete'));
    if (state) {
      target.dataset.promoCount = state.count;
      target.dataset.promoInSet = state.inSet;
      target.dataset.promoComplete = String(state.complete);
    }

    const stateTag = target.querySelector('script[type="application/json"][data-promo-state]');
    const freshStateTag = fresh.querySelector('script[type="application/json"][data-promo-state]');
    if (stateTag && freshStateTag) stateTag.textContent = freshStateTag.textContent;

    syncCartState(state);

    // Slot markup was just replaced, so rebuild the buttons on top of it.
    enhanceSlots(target);
    // The basket moved, so an earlier dismissal may no longer apply.
    applyDismissal(target);
  };

  /** Apply a cart-items HTML string to every module outside the cart. */
  const applyCartHTML = (html) => {
    const targets = externalModules();
    if (!targets.length || !html) return false;

    const fresh = parse(html).querySelector(ROOT_SELECTOR);
    if (!fresh) return false;

    targets.forEach((target) => repaint(target, fresh));
    return true;
  };

  /* ---------------------------------------------------------------- tracking */

  /**
   * Two independent channels, because they answer different questions.
   *
   * 1. Shopify.analytics.publish — the supported bridge from theme code into
   *    Web Pixels. The store's GA4 runs inside that sandbox, so theme JS cannot
   *    call gtag directly; a Custom Pixel subscribes to these names instead.
   *    See _promo/INSTALL.md section 6 for the pixel to paste in.
   *
   * 2. Cart attributes — written onto the cart, so they survive to the order.
   *    This is what actually answers "did the widget raise basket value",
   *    because you can segment orders on it in Shopify's own reports and
   *    exports without any analytics vendor at all.
   *
   * Everything here is best-effort. Tracking must never break the shop, so all
   * of it is wrapped and all of it fails silently.
   */

  const EVENT_PREFIX = 'promo_progress_';

  const track = (name, payload = {}) => {
    const body = { promotion: '3for', ...payload };
    try {
      window.Shopify?.analytics?.publish?.(EVENT_PREFIX + name, body);
    } catch (e) {
      /* pixel sandbox unavailable */
    }
    // Also a plain DOM event, so GTM or anything else can listen without
    // depending on Shopify's pixel layer.
    try {
      document.dispatchEvent(
        new CustomEvent(EVENT_PREFIX + name, { detail: body })
      );
    } catch (e) {
      /* nothing to do */
    }
  };

  /**
   * Merge attributes onto the cart. /cart/update.js merges rather than
   * replaces, so this cannot clobber attributes set by anything else. Keys are
   * underscore-prefixed, which keeps them out of the customer's view while
   * still landing on the order.
   *
   * This does not go through Shapes' cart JS, so it raises none of its events
   * and cannot start a repaint loop.
   */
  const WRITTEN_KEY = 'locketloves:promo-progress:written';

  const readWritten = () => {
    try {
      return JSON.parse(sessionStorage.getItem(WRITTEN_KEY)) || {};
    } catch (e) {
      return {};
    }
  };

  /**
   * Record what the cart ACTUALLY says after a write, not what we hoped it
   * would say. Anything that did not land is simply absent here, so the next
   * page load tries it again. Optimistic bookkeeping would turn a single lost
   * write into a permanent one.
   */
  const recordServerAttributes = (attributes) => {
    const mine = {};
    Object.keys(attributes || {}).forEach((key) => {
      if (key.indexOf('_promo_') === 0) mine[key] = String(attributes[key]);
    });
    try {
      sessionStorage.setItem(WRITTEN_KEY, JSON.stringify(mine));
    } catch (e) {
      /* Storage blocked. Writes still go out, just without dedupe. */
    }
  };

  /**
   * Merge attributes onto the cart. /cart/update.js merges rather than
   * replaces, so this cannot clobber attributes set by anything else. Keys are
   * underscore-prefixed, which keeps them out of the customer's view while
   * still landing on the order.
   *
   * Writes are idempotent and re-asserted from the cart state on every page
   * load. That matters more than it sounds: this store navigates to /cart on
   * add, so a write fired from the add event races a navigation that cancels
   * it. The re-assert on the /cart page load has no navigation in flight, so
   * a lost write heals itself on the very next page view instead of being
   * gone for good. `written` stops the re-assert costing a request when
   * nothing has changed.
   */
  let pendingAttributes = null;
  let flushScheduled = false;

  const flushAttributes = () => {
    flushScheduled = false;
    const attributes = pendingAttributes;
    pendingAttributes = null;
    if (!attributes) return;

    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ attributes }),
        // Still worth asking for on the racing path, even though the page-load
        // re-assert is what actually makes this reliable.
        keepalive: true,
      })
        .then((res) => (res && res.ok ? res.json() : null))
        .then((cart) => {
          if (cart && cart.attributes) recordServerAttributes(cart.attributes);
        })
        .catch(() => {});
    } catch (e) {
      /* Progress tracking only. Never surface this to the shopper. */
    }
  };

  const setCartAttributes = (attributes) => {
    const written = readWritten();

    Object.keys(attributes).forEach((key) => {
      const value = String(attributes[key]);
      if (written[key] === value) return;
      if (pendingAttributes && pendingAttributes[key] === value) return;
      pendingAttributes = pendingAttributes || {};
      pendingAttributes[key] = value;
    });

    if (!pendingAttributes || flushScheduled) return;
    flushScheduled = true;
    // Batched to exactly one request per turn. The cart is a single record, so
    // two POSTs fired in the same tick race each other and one is silently
    // lost — which is how the surface write ate the set write in testing.
    Promise.resolve().then(flushAttributes);
  };

  const PICKER_KEY = 'locketloves:promo-progress:pickerAdds';

  const readPickerAdds = () => {
    try {
      return Number(sessionStorage.getItem(PICKER_KEY)) || 0;
    } catch (e) {
      return 0;
    }
  };

  const session = {
    viewed: new Set(),
    // Held in sessionStorage, not just in memory: this store navigates to
    // /cart on every add, so an in-memory counter would reset to 1 each time.
    get pickerAdds() {
      return readPickerAdds();
    },
    set pickerAdds(n) {
      try {
        sessionStorage.setItem(PICKER_KEY, String(n));
      } catch (e) {
        /* storage blocked */
      }
    },
  };

  /**
   * The surface that drove the most recent add, held until the cart state
   * confirms the add actually qualified.
   *
   * The previous version recorded a surface on every add anywhere on the site,
   * with no check that the added product carried the promotion tag. That is
   * why _promo_surface landed on orders containing no promotion product at
   * all. Nothing is written now until the qualifying count actually rises.
   */
  const SURFACE_KEY = 'locketloves:promo-progress:pendingSurface';
  const SURFACE_TTL = 60 * 1000;

  // Fallback for private modes where storage throws.
  let pendingSurface = null;

  /**
   * Held in sessionStorage rather than memory because an add on this store is
   * a form POST that navigates to /cart. An in-memory value would die with the
   * page before the new cart state could confirm the add qualified.
   */
  const notePendingSurface = (surface) => {
    const held = { surface, at: Date.now() };
    pendingSurface = held;
    try {
      sessionStorage.setItem(SURFACE_KEY, JSON.stringify(held));
    } catch (e) {
      /* memory fallback already set */
    }
  };

  const takePendingSurface = () => {
    let held = null;
    try {
      held = JSON.parse(sessionStorage.getItem(SURFACE_KEY));
      sessionStorage.removeItem(SURFACE_KEY);
    } catch (e) {
      /* fall back to memory */
    }
    held = held || pendingSurface;
    pendingSurface = null;
    if (!held || Date.now() - held.at > SURFACE_TTL) return null;
    return held.surface;
  };

  /** Fire once per role per page load. */
  const trackView = (root) => {
    const role = root.dataset.promoRole || 'unknown';
    if (session.viewed.has(role)) return;
    session.viewed.add(role);
    track('viewed', {
      surface: role,
      qualifying_count: Number(root.dataset.promoCount || 0),
      complete: root.dataset.promoComplete === 'true',
    });
  };

  /**
   * Watch for the set completing during this visit. Deliberately not fired for
   * someone who arrives with a full basket already: that is not the module
   * doing any work, and counting it would flatter the numbers.
   *
   * The baseline lives in sessionStorage rather than a variable, because this
   * store navigates to /cart on every add. An in-memory baseline would be
   * reseeded on each page load and the transition would never be seen.
   */
  const SEEN_KEY = 'locketloves:promo-progress:lastCount';

  const readLastCount = () => {
    try {
      const v = sessionStorage.getItem(SEEN_KEY);
      return v === null ? null : Number(v);
    } catch (e) {
      return null;
    }
  };

  const writeLastCount = (n) => {
    try {
      sessionStorage.setItem(SEEN_KEY, String(n));
    } catch (e) {
      /* storage blocked */
    }
  };

  /**
   * Reconcile the cart's promotion state onto the order and fire the
   * behavioural events. This is the single place that decides what an order
   * ends up saying, and it runs on every page load as well as after every
   * repaint.
   *
   * The old version only ever wrote on a transition it had personally
   * witnessed, which meant one missed event lost the fact for good. It never
   * once landed: not a single order carried _promo_completed. What goes on the
   * order now is derived from cart state, so it is true whether or not any
   * particular event was delivered.
   */
  const syncCartState = (state) => {
    if (!state) return;

    const previous = readLastCount();
    writeLastCount(state.count);

    // A qualifying add just happened and we know what drove it. Attribution is
    // first-touch, so an existing value is never overwritten.
    if (previous !== null && state.count > previous) {
      const surface = takePendingSurface();
      if (surface && !readWritten()._promo_surface) {
        setCartAttributes({ _promo_surface: surface });
      }
    }

    // Writing to an empty cart would create a cart token for a shopper who has
    // added nothing, which pollutes abandoned-cart reporting. Say nothing until
    // there is a real basket to describe.
    if (!state.cartCount) return;

    // The state of the basket as it stands. Idempotent, so re-asserting it
    // costs nothing, and it stays correct when a shopper removes items.
    // _promo_completed is written on every real basket, false included: that
    // way "no set" and "tracking never ran" stop looking identical in an
    // export, which is exactly the ambiguity that made week one unreadable.
    setCartAttributes({
      _promo_sets: String(state.sets),
      _promo_completed: state.sets > 0 ? 'true' : 'false',
    });

    // Sets completed during this visit, kept separate and best-effort.
    // Deliberately not counted for someone who arrived with a full basket
    // already: that is not the module doing any work.
    if (previous !== null && state.qty > 0) {
      const previousSets = Math.floor(previous / state.qty);
      if (state.sets > previousSets) {
        track('set_completed', {
          sets: state.sets,
          sets_added: state.sets - previousSets,
          qualifying_count: state.count,
          picker_adds: session.pickerAdds,
        });
        setCartAttributes({ _promo_visit_completed: 'true' });
      }
    }
  };

  /* ------------------------------------------------------- slots as buttons */

  /**
   * Turn empty slots into buttons that open the picker.
   *
   * Done here rather than in Liquid on purpose. The cart copy of the module has
   * no picker, and the repaint below copies slot markup from the cart render
   * into the product page copy. Enhancing after every paint keeps the two
   * concerns separate: Liquid always emits the same inert slots, and this makes
   * them interactive only where there is something for them to open.
   *
   * It also means no-JS shoppers get plain slots instead of dead buttons.
   */
  const enhanceSlots = (root) => {
    const picker = root.querySelector('[data-promo-picker]');
    if (!picker) return;

    const expanded = !picker.hidden;
    const empties = root.querySelectorAll('.promo-progress__slot--empty');

    empties.forEach((slot, i) => {
      if (slot.querySelector('[data-promo-slot-pick]')) {
        slot.querySelector('[data-promo-slot-pick]')
          .setAttribute('aria-expanded', String(expanded));
        return;
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'promo-progress__slot-btn';
      btn.setAttribute('data-promo-slot-pick', '');
      btn.setAttribute('aria-expanded', String(expanded));
      btn.setAttribute('aria-controls', picker.id);
      btn.innerHTML = slot.innerHTML;

      // A dashed box with a number in it does not read as a control. Give the
      // slot the same corner affordance the filled slots use for their tick.
      const plus = document.createElement('span');
      plus.className = 'promo-progress__add';
      plus.setAttribute('aria-hidden', 'true');
      plus.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
      btn.appendChild(plus);

      const label = document.createElement('span');
      label.className = 'sr-only';
      label.textContent = `Choose print ${i + 1}. Shows popular leggings in this offer.`;
      btn.appendChild(label);

      slot.replaceChildren(btn);
      slot.classList.add('promo-progress__slot--interactive');
    });
  };

  /* ------------------------------------------------------------------ picker */

  let lastOpener = null;

  const setPicker = (root, open) => {
    const picker = root.querySelector('[data-promo-picker]');
    if (!picker) return;

    picker.hidden = !open;
    root
      .querySelectorAll('[data-promo-slot-pick]')
      .forEach((b) => b.setAttribute('aria-expanded', String(open)));

    if (open) {
      const heading = picker.querySelector('[data-promo-picker-heading]');
      // Focus without the browser's own jump, then scroll deliberately. The
      // picker renders well below the meter, so an unscrolled open looks
      // exactly like nothing happening.
      if (heading) {
        heading.focus({ preventScroll: true });
        heading.scrollIntoView({
          block: 'center',
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-promo-slot-pick]');
    if (opener) {
      const root = opener.closest(ROOT_SELECTOR);
      const picker = root && root.querySelector('[data-promo-picker]');
      if (!picker) return;
      event.preventDefault();
      lastOpener = opener;
      // Always reveal, never collapse. The picker ships open, so a plain
      // toggle made the shopper's first click close a panel sitting hundreds
      // of pixels below the fold — indistinguishable from a broken button.
      // Escape still closes it and returns focus to the slot.
      const wasHidden = picker.hidden;
      setPicker(root, true);
      if (wasHidden) {
        track('picker_opened', {
          qualifying_count: Number(root.dataset.promoCount || 0),
        });
      }
      return;
    }

    const cta = event.target.closest('[data-promo-cta] a');
    if (cta) {
      const root = cta.closest(ROOT_SELECTOR);
      track('cta_clicked', {
        surface: root?.dataset.promoRole,
        qualifying_count: Number(root?.dataset.promoCount || 0),
        destination: cta.getAttribute('href'),
      });
      // Not prevented: the link must still navigate.
    }

    const dismiss = event.target.closest('[data-promo-dismiss]');
    if (dismiss) {
      event.preventDefault();
      const root = dismiss.closest(ROOT_SELECTOR);
      const count = root ? root.dataset.promoCount : '0';
      try {
        localStorage.setItem(
          DISMISS_KEY,
          JSON.stringify({ at: Date.now(), count: Number(count) })
        );
      } catch (e) {
        /* Storage blocked. Still hide it for this page view. */
      }
      track('dismissed', {
        surface: root?.dataset.promoRole,
        qualifying_count: Number(count),
      });

      // Hide every copy, not just the one that was clicked.
      document.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
        el.hidden = true;
      });
    }
  });

  // Escape closes the picker and returns focus where it came from.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const picker = event.target.closest('[data-promo-picker]');
    if (!picker || picker.hidden) return;
    const root = picker.closest(ROOT_SELECTOR);
    setPicker(root, false);
    if (lastOpener && lastOpener.isConnected) lastOpener.focus();
  });

  /* ---------------------------------------------------------------- dismissal */

  /**
   * Re-show the module when the qualifying count moves away from whatever it
   * was when the shopper dismissed it. At that point they are engaging with the
   * offer and the meter is progress information, not an advert.
   */
  const applyDismissal = (root) => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(DISMISS_KEY) || 'null');
    } catch (e) {
      return;
    }
    if (!stored) return;

    const stale = Date.now() - stored.at > DISMISS_TTL;
    const moved = String(stored.count) !== root.dataset.promoCount;

    if (stale || moved) {
      root.hidden = false;
      if (stale) {
        try {
          localStorage.removeItem(DISMISS_KEY);
        } catch (e) {
          /* nothing to do */
        }
      }
    } else {
      root.hidden = true;
    }
  };

  /* --------------------------------------------------- picker add attribution */

  /**
   * Caught on submit, in the capture phase, rather than waiting for Shapes'
   * post-add event. This store navigates to /cart on add, so the post-add event
   * may never be delivered. Submit always fires first.
   */
  let lastPickerSubmit = 0;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.closest || !form.getAttribute) return;
      if (!(form.getAttribute('action') || '').includes('/cart/add')) return;

      const inPicker = !!form.closest('[data-promo-picker]');

      if (inPicker) {
        lastPickerSubmit = Date.now();
        session.pickerAdds = session.pickerAdds + 1;
        track('add_from_picker', { picker_adds: session.pickerAdds });
        // Every tile in the picker comes from the promotion collection, so a
        // picker add always qualifies and can be recorded straight away.
        setCartAttributes({ _promo_picker_adds: String(session.pickerAdds) });
      }

      // Every add-to-cart submit, not just the picker's. On the navigate flow
      // this is the only moment we can see what the shopper actually used.
      notePendingSurface(
        inPicker ? 'picker' : form.closest(ROOT_SELECTOR) ? 'module' : 'page'
      );
    },
    true
  );

  /* ------------------------------------------------------------------ events */

  let lastPayloadAt = 0;

  const onShapesPayload = (event) => {
    // Attribute the add before repainting, while originalTarget still points at
    // the control the shopper actually used.
    const origin = event?.detail?.originalTarget;
    if (origin && origin.closest) {
      const fromPicker =
        origin.closest('[data-promo-picker]') || Date.now() - lastPickerSubmit < 3000;
      // Held, not written. syncCartState decides whether the add qualified.
      notePendingSurface(
        fromPicker ? 'picker' : origin.closest(ROOT_SELECTOR) ? 'module' : 'page'
      );
    }

    const html = event?.detail?.response?.sections?.['cart-items'];
    if (!html) return;
    lastPayloadAt = Date.now();
    applyCartHTML(html);
  };

  // Primary path. Shapes has already paid for this network request, so riding
  // its payload costs us nothing.
  window.addEventListener('shapes:cart:afteradditem', onShapesPayload);
  window.addEventListener('shapes:cart:cartqtychange', onShapesPayload);
  window.addEventListener('shapes:cart:update', onShapesPayload);

  /**
   * Safety net. `theme:cart:change` is the documented public event and fires
   * after the cart sections have been swapped. If it arrives without one of the
   * payload events having fired just before it (a code path we have not seen,
   * or a future Shapes refactor), fetch the section ourselves. One request,
   * only when the free ride failed.
   */
  /* ------------------------------------------------------------------- init */

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      enhanceSlots(root);
      applyDismissal(root);
      if (!root.hidden) trackView(root);
    });
  };

  /**
   * Reconcile from the server render on every page load. This is the reliable
   * path: the navigate-to-cart flow lands here with no request in flight to
   * cancel it, so anything a racing add-event write lost is put right here.
   */
  const syncFromDOM = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (root) syncCartState(readState(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      syncFromDOM();
    });
  } else {
    init();
    syncFromDOM();
  }

  // The cart copy is re-rendered by Shapes' own Alpine.morph, which replaces
  // slot markup wholesale, so re-enhance after the drawer opens too.
  document.addEventListener('theme:cart-drawer:open', init);

  document.addEventListener('theme:cart:change', () => {
    // Shapes has already morphed the cart module by now, so its state script is
    // fresh. On the /cart page there are no external modules to repaint, which
    // means this is the only chance to reconcile after an in-page add.
    syncFromDOM();

    if (Date.now() - lastPayloadAt < 1500) return;
    if (!externalModules().length) return;

    fetch(`${window.location.pathname}?sections=cart-items`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => applyCartHTML(data['cart-items']))
      .catch(() => {
        /* Progress display only. A failure here must never block the cart. */
      });
  });
})();
